// screens/ComplaintNewScreen.js - Register a complaint on an order number.
// Step 1 looks the order up (what PhotoQA and Blueware know, and whether a dossier
// already exists); step 2 takes the references that key every service mail thread:
// retailer reference, consumer name, service partner and its case number.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from './HomeScreen';
import { SyncBar, useSmartBack } from '../components/shared';
import { useLang } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import useComplaintTaxonomy from '../hooks/useComplaintTaxonomy';
import api, { getErrorMessage } from '../utils/api';
import { formatDay } from '../utils/complaints';

export default function ComplaintNewScreen() {
  const navigate = useNavigate();
  const back = useSmartBack('/complaints/list');
  const { t } = useLang();
  const toast = useToast();
  const { taxonomy } = useComplaintTaxonomy();
  const [orderNumber, setOrderNumber] = useState('');
  const [lookup, setLookup] = useState(null);
  const [looking, setLooking] = useState(false);
  const [form, setForm] = useState({ retailer_ref: '', consumer_name: '', service_partner: '', service_case_ref: '', notes: '' });
  const [creating, setCreating] = useState(false);

  const doLookup = async (e) => {
    e?.preventDefault();
    const n = orderNumber.trim();
    if (!n) return;
    setLooking(true);
    setLookup(null);
    try {
      const { data } = await api.get('/api/complaints/lookup', { params: { order_number: n } });
      setLookup(data);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLooking(false); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const body = { order_number: lookup.order_number || orderNumber.trim() };
      Object.entries(form).forEach(([k, v]) => { if (v && String(v).trim()) body[k] = String(v).trim(); });
      const { data } = await api.post('/api/complaints', body);
      const id = data?.complaint?.id || data?.id;
      toast.success(t('complaints.created'));
      navigate(`/complaints/${id}`, { replace: true, state: { addLine: true } });
    } catch (err) {
      // One dossier per order: someone registered it in the meantime, so continue there.
      const existing = err?.response?.status === 409 && err.response.data?.existing_complaint_id;
      if (existing) {
        toast.info(t('complaints.exists'));
        navigate(`/complaints/${existing}`, { replace: true, state: { addLine: true } });
        return;
      }
      toast.error(getErrorMessage(err));
      setCreating(false);
    }
  };

  const bw = lookup?.blueware;
  const nowhere = lookup && !lookup.found_in_photoqa && !bw;

  return (
    <div className="screen">
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={back} aria-label={t('complaints.back')}>←</button>
        <h2 style={{ flex: 1, textAlign: 'center' }}>{t('complaints.new')}</h2>
        <div style={{ width: 52 }} />
      </div>

      <div className="page-content" style={{ maxWidth: 760, width: '100%', margin: '0 auto' }}>
        <form className="card" style={{ marginBottom: 16 }} onSubmit={doLookup}>
          <label className="form-label" htmlFor="c-order">{t('complaints.ordernumber')}</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input id="c-order" className="input-field" value={orderNumber} autoFocus
              style={{ fontFamily: 'var(--font-mono)' }}
              onChange={(e) => { setOrderNumber(e.target.value); setLookup(null); }}
              placeholder="CR8-..." />
            <button type="submit" className="btn btn-primary" disabled={!orderNumber.trim() || looking}>
              {looking ? t('complaints.lookingup') : t('complaints.lookup')}
            </button>
          </div>
          <div className="form-hint" style={{ marginTop: 8 }}>{t('complaints.lookup.hint')}</div>
        </form>

        {lookup && (
          <div className="card" style={{ marginBottom: 16 }}>
            {lookup.existing_complaint_id && (
              <div className="warning-banner" style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('complaints.exists')}</div>
                <div style={{ marginBottom: 10 }}>{t('complaints.exists.msg')}</div>
                <button className="btn btn-primary btn-sm" onClick={() => navigate(`/complaints/${lookup.existing_complaint_id}`, { state: { addLine: true } })}>
                  {t('complaints.exists.open')}
                </button>
              </div>
            )}
            {nowhere && <div className="warning-banner" style={{ marginBottom: 14 }}>{t('complaints.notfound')}</div>}

            <div className="section-title">{t('complaints.known')}</div>
            <div className="meta-grid" style={{ marginBottom: 12 }}>
              <Meta label="PhotoQA" value={lookup.found_in_photoqa ? t('complaints.found') : t('complaints.notfound.short')} />
              <Meta label={t('complaints.model')} value={lookup.model || bw?.model} />
              <Meta label={t('complaints.factory')} value={lookup.factory} />
              <Meta label={lookup.production_date_source === 'delivery' ? t('complaints.deliverydate') : t('complaints.productiondate')}
                value={lookup.production_date ? formatDay(lookup.production_date) : null}
                flag={lookup.production_date_source === 'delivery' ? t('complaints.fallback') : null} />
              <Meta label="Blueware" value={bw ? t('complaints.found') : t('complaints.notfound.short')} />
              {bw && <Meta label={t('complaints.customer')} value={[bw.customer_code, bw.customer_name].filter(Boolean).join(' · ')} />}
              {bw?.customer_variant && <Meta label={t('complaints.variant')} value={bw.customer_variant} />}
              {bw && <Meta label={t('complaints.fabric')} value={bw.fabric} />}
              {bw && <Meta label={t('complaints.delivered')} value={bw.delivery_date ? formatDay(bw.delivery_date) : null} />}
            </div>
            {Array.isArray(bw?.open_service_orders) && bw.open_service_orders.length > 0 && (
              <div className="form-hint">
                {t('complaints.serviceorders')}: {bw.open_service_orders.map((s) => s.order_no).join(', ')}
              </div>
            )}
          </div>
        )}

        {lookup && !lookup.existing_complaint_id && (
          <div className="card">
            <div className="section-title">{t('complaints.refs')}</div>
            <RefsForm form={form} setForm={setForm} partners={taxonomy?.servicePartners || []} t={t} />
            <button className="btn btn-primary btn-full" style={{ marginTop: 18 }} disabled={creating} onClick={create}>
              {creating ? t('complaints.creating') : t('complaints.create')}
            </button>
            <div className="form-hint" style={{ marginTop: 8 }}>{t('complaints.create.hint')}</div>
          </div>
        )}
      </div>

      <BottomNav active="complaints" />
    </div>
  );
}

function Meta({ label, value, flag }) {
  return (
    <div>
      <div className="meta-label">{label}</div>
      <div className="meta-value">
        {value || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>–</span>}
        {flag && <span className="badge badge-warning" style={{ fontSize: '0.625rem', marginLeft: 6 }}>{flag}</span>}
      </div>
    </div>
  );
}

// Shared by the new-complaint screen and the dossier header.
export function RefsForm({ form, setForm, partners, t }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <div className="form-group">
          <label className="form-label" htmlFor="ref-retailer">{t('complaints.retailerref')}</label>
          <input id="ref-retailer" className="input-field" value={form.retailer_ref || ''} onChange={set('retailer_ref')} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="ref-consumer">{t('complaints.consumer')}</label>
          <input id="ref-consumer" className="input-field" value={form.consumer_name || ''} onChange={set('consumer_name')} />
        </div>
      </div>
      <div className="form-group">
        <span className="form-label">{t('complaints.partner')}</span>
        <div className="tile-grid" role="group" aria-label={t('complaints.partner')}>
          {partners.map((p) => (
            <button key={p} type="button" className={`tile ${form.service_partner === p ? 'selected' : ''}`}
              aria-pressed={form.service_partner === p}
              onClick={() => setForm((f) => ({ ...f, service_partner: f.service_partner === p ? '' : p }))}>
              {p}
            </button>
          ))}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="ref-case">{t('complaints.caseref')}</label>
        <input id="ref-case" className="input-field" value={form.service_case_ref || ''} onChange={set('service_case_ref')} />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="ref-notes">{t('complaints.notes')}</label>
        <textarea id="ref-notes" className="input-field" rows={2} style={{ minHeight: 64 }} value={form.notes || ''} onChange={set('notes')} />
      </div>
    </div>
  );
}
